import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import './GoraiLandingPage.css';

const GoraiLandingPage = () => {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);

  return (
    <div className="gorai-landing-page">
      {/* Animated Background */}
      <motion.div className="gorai-landing-bg" style={{ y: backgroundY }}>
        <div className="gorai-bg-gradient" />
        <div className="circuit-pattern circuit-1" />
        <div className="circuit-pattern circuit-2" />
        <div className="circuit-pattern circuit-3" />
      </motion.div>

      {/* Hero Section */}
      <section className="gorai-hero-section">
        <div className="gorai-hero-content">
          <motion.div
            className="gorai-hero-badge"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <span className="gorai-badge-icon">🚀</span>
            <span>Go + Robot + AI</span>
          </motion.div>
          
          <motion.div
            className="gorai-hero-title-container"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <img src="/images/gorai.png" alt="Gorai" className="gorai-hero-logo" />
            <h1 className="gorai-hero-title">Gorai</h1>
          </motion.div>
          
          <motion.p
            className="gorai-hero-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            The Robotics Platform for the AI Era
          </motion.p>
          
          <motion.p
            className="gorai-hero-description"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            Build robots like software. Run them like systems. AI agents, replay, and fleet 
            operations as first-class citizens—not bolt-ons. Productive in days, not months.
          </motion.p>

          <motion.div
            className="gorai-hero-cta"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            <Link to="/gorai/docs" className="gorai-cta-primary">
              Explore Documentation
            </Link>
            <a href="#why-gorai" className="gorai-cta-secondary">
              Learn More ↓
            </a>
          </motion.div>
        </div>

        <motion.div
          className="gorai-hero-image-container"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          <div className="gorai-hero-image-glow" />
          <div className="gorai-hero-image-glow-accent" />
          <img 
            src="/images/gorai.png" 
            alt="Gorai Framework"
            className="gorai-hero-image"
          />
        </motion.div>

        <div className="gorai-scroll-indicator">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <span className="gorai-scroll-arrow">↓</span>
            <p>Scroll to explore</p>
          </motion.div>
        </div>
      </section>

      {/* Why Gorai Section */}
      <section className="gorai-why-section" id="why-gorai">
        <div className="gorai-section-container">
          <motion.div
            className="gorai-why-header"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <h2>Why Gorai?</h2>
            <p className="gorai-section-subtitle">
              Physical AI is here. Decision-making is moving up the stack—from hand-authored logic 
              to learned, probabilistic, and agentic systems. That shift changes what a platform 
              must provide: not just motor control, but AI-first orchestration, safety, and auditability.
            </p>
          </motion.div>

          <div className="gorai-target-grid">
            <motion.div
              className="gorai-target-column use"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h3>✅ Use Gorai If You:</h3>
              <ul>
                <li>Want <strong>real autonomy</strong> where behavior and coordination are more complex than kinematics</li>
                <li>Are a <strong>software-first team</strong> adding physical embodiment to AI/ML systems</li>
                <li>Need <strong>AI-driven execution</strong> as a first-class concern (with safety and governance)</li>
                <li>Value <strong>replay and auditability</strong> as baseline, not add-ons</li>
                <li>Care about <strong>scaling from one robot to fleets</strong> without rewriting</li>
                <li>Want <strong>approachable tooling</strong> (productive in days, not months)</li>
              </ul>
            </motion.div>

            <motion.div
              className="gorai-target-column avoid"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h3>❌ Use ROS 2 If You:</h3>
              <ul>
                <li>Work in <strong>enterprise/research</strong> robotics (warehouse automation, autonomous vehicles)</li>
                <li>Need the <strong>full ROS ecosystem</strong> (thousands of packages, SLAM, simulation)</li>
                <li>Are in <strong>academia</strong> where ROS 2 is the standard</li>
                <li>Need <strong>deep hardware experimentation</strong> (ROS 2 is the right platform)</li>
              </ul>
            </motion.div>
          </div>

          <motion.p
            className="gorai-target-note"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <strong>We're not replacing ROS 2.</strong> We're targeting teams whose hardest problems 
            are autonomy, orchestration, deployment, and safety—not low-level robotics research.
          </motion.p>
        </div>
      </section>

      {/* Cloud Native Advantage */}
      <section className="gorai-advantage-section">
        <div className="gorai-section-container">
          <motion.div
            className="gorai-advantage-header"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <span className="gorai-enter-badge">Key Principles</span>
            <h2>Built for Physical AI</h2>
            <p className="gorai-section-subtitle">
              Gorai is designed around three foundational principles that reflect the shift 
              to physical AI: autonomy without replay is folklore, AI agents are first-class 
              but not mandatory, and robots should be built like software systems.
            </p>
          </motion.div>

          <div className="gorai-comparison-table">
            <motion.div
              className="gorai-table-wrapper"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <table>
                <thead>
                  <tr>
                    <th>Principle</th>
                    <th>What It Means</th>
                    <th>Why It Matters</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Replay First</strong></td>
                    <td>Action logs and state streams are platform concerns</td>
                    <td>Debug, audit, and improve autonomy over time</td>
                  </tr>
                  <tr>
                    <td><strong>Agent-Compatible</strong></td>
                    <td>AI-driven execution alongside state machines and scripts</td>
                    <td>No autonomy method is mandatory—all are constrained</td>
                  </tr>
                  <tr>
                    <td><strong>Software-First</strong></td>
                    <td>If you think in APIs and systems, you're productive in days</td>
                    <td>Lower cognitive overhead, faster iteration</td>
                  </tr>
                  <tr>
                    <td><strong>Fleet-Ready</strong></td>
                    <td>Same contracts from one robot to many</td>
                    <td>Scale without rewriting architecture</td>
                  </tr>
                  <tr>
                    <td><strong>Safety by Design</strong></td>
                    <td>Constraints, permissions, and governance at runtime</td>
                    <td>AI suggests. Safety enforces. Control executes.</td>
                  </tr>
                </tbody>
              </table>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="gorai-features-section">
        <div className="gorai-section-container">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Design Principles
          </motion.h2>

          <div className="gorai-features-grid">
            {[
              { 
                icon: '🤖', 
                title: 'AI-First by Design', 
                desc: 'AI agents as first-class executors. Capability surfaces, governance, and safety built into the platform—not bolted on.' 
              },
              { 
                icon: '📼', 
                title: 'Replay as Baseline', 
                desc: 'Action logs and state streams are platform concerns. Debug, audit, and improve autonomy over time.' 
              },
              { 
                icon: '🛡️', 
                title: 'Safety & Governance', 
                desc: 'Runtime constraints, permissions, and overrides. AI suggests. Safety enforces. Control executes.' 
              },
              { 
                icon: '📈', 
                title: 'Progressive Scaling', 
                desc: 'Start simple, scale without rewriting. One binary today; same contracts for fleets tomorrow.' 
              },
              { 
                icon: '🎯', 
                title: 'Clarity Over Flexibility', 
                desc: 'Opinionated, pragmatic, operational. Optimized for software-first teams.' 
              },
              { 
                icon: '🔌', 
                title: 'Polyglot Services', 
                desc: 'Go core, but Python/C++ services via NATS. Use the best tool for each job.' 
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="gorai-feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <span className="gorai-feature-icon">{item.icon}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture Section */}
      <section className="gorai-architecture-section">
        <div className="gorai-section-container">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Simple by Default, Powerful by Design
          </motion.h2>

          <motion.p
            className="gorai-section-subtitle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            Start with a single Go binary and NATS. No containers, no K8s required. 
            When you need ML services or fleet orchestration, add them without rewriting. 
            Same RDL, same contracts, same programming model.
          </motion.p>

          <div className="gorai-arch-diagram">
            <motion.div
              className="gorai-arch-box user-experience"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h3>What Users See</h3>
              <div className="gorai-arch-flow">
                <code>robot.rdl.json</code>
                <span className="gorai-arrow">→</span>
                <code>gorai run</code>
                <span className="gorai-arrow">→</span>
                <span>Robot running</span>
              </div>
              <p>Users work with: Robot Definition Language (JSON)</p>
              <p className="gorai-muted">Output: Single binary (~10-20MB)</p>
            </motion.div>

            <motion.div
              className="gorai-arch-box actual-runs"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <h3>What Actually Runs</h3>
              <div className="gorai-arch-tree">
                <div className="gorai-tree-item">Single Go Binary</div>
                <div className="gorai-tree-children">
                  <div className="gorai-tree-item">├── Components (GPS, motors, sensors)</div>
                  <div className="gorai-tree-item">├── Message Router (NATS client)</div>
                  <div className="gorai-tree-item">├── Behaviors (state machines, AI agents)</div>
                  <div className="gorai-tree-item">└── NATS Server (systemd service)</div>
                </div>
              </div>
              <p className="gorai-muted">Phase 2: Add optional containers for ML/vision</p>
              <p className="gorai-muted">Phase 3: Add K3s for fleet management</p>
            </motion.div>
          </div>

          <motion.div
            className="gorai-hardware-req"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <h3>Hardware Platforms</h3>
            <div className="gorai-hw-grid">
              <div className="gorai-hw-item recommended">
                <span className="gorai-hw-label">Recommended</span>
                <span className="gorai-hw-value">Raspberry Pi 5 (8GB)</span>
                <span className="gorai-hw-cost">~$160 • Hailo AI accelerator support</span>
              </div>
              <div className="gorai-hw-item">
                <span className="gorai-hw-label">Budget</span>
                <span className="gorai-hw-value">Orange Pi 5B (8GB)</span>
                <span className="gorai-hw-cost">~$145 • 6 TOPS built-in NPU</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Target Users Section */}
      <section className="gorai-users-section">
        <div className="gorai-section-container">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Built For You
          </motion.h2>

          <div className="gorai-users-grid">
            {[
              { 
                emoji: '🤖', 
                title: 'Software-First Teams', 
                desc: 'Adding physical embodiment to AI/ML systems. You think in APIs and distributed systems.' 
              },
              { 
                emoji: '🚀', 
                title: 'Scaling to Fleets', 
                desc: 'Going from one robot to many without re-architecting. Fleet operations from day one.' 
              },
              { 
                emoji: '🔬', 
                title: 'Research & Monitoring', 
                desc: 'Autonomous systems for data collection, environmental monitoring, and research platforms.' 
              },
              { 
                emoji: '🎓', 
                title: 'Students & Educators', 
                desc: 'Teaching modern robotics without PhD toolchains. Productive in days, focus on autonomy.' 
              },
            ].map((user, index) => (
              <motion.div
                key={index}
                className="gorai-user-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <span className="gorai-user-emoji">{user.emoji}</span>
                <h3>{user.title}</h3>
                <p>{user.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Start Section */}
      <section className="gorai-quickstart-section">
        <div className="gorai-section-container">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Quick Start
          </motion.h2>

          <div className="gorai-quickstart-steps">
            <motion.div
              className="gorai-step"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="gorai-step-num">1</span>
              <div className="gorai-step-content">
                <h3>Write Configuration</h3>
                <pre><code>cat &gt; robot.rdl.json</code></pre>
              </div>
            </motion.div>

            <motion.div
              className="gorai-step"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <span className="gorai-step-num">2</span>
              <div className="gorai-step-content">
                <h3>Validate & Run</h3>
                <pre><code>gorai validate robot.rdl.json<br/>gorai run robot.rdl.json</code></pre>
              </div>
            </motion.div>

            <motion.div
              className="gorai-step"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <span className="gorai-step-num">3</span>
              <div className="gorai-step-content">
                <h3>Build for Deployment</h3>
                <pre><code>gorai build robot.rdl.json -o robot</code></pre>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gorai-cta-section">
        <div className="gorai-section-container">
          <motion.div
            className="gorai-cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Ready to Get Started?</h2>
            <p>
              Explore our documentation, review the design decisions, 
              and join us in making professional robotics accessible to everyone.
            </p>
            <div className="gorai-cta-buttons">
              <Link to="/gorai/docs" className="gorai-cta-primary">
                📖 Documentation
              </Link>
              <Link to="/gorai/info" className="gorai-cta-secondary">
                ℹ️ Strategic Info
              </Link>
              <a 
                href="https://github.com/emergingrobotics/gorai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="gorai-cta-secondary"
              >
                ⭐ Star on GitHub
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="gorai-landing-footer">
        <div className="gorai-section-container">
          <div className="gorai-footer-content">
            <div className="gorai-footer-brand">
              <span className="gorai-footer-logo">
                <img src="/images/gorai.png" alt="Gorai" className="gorai-footer-logo-img" />
                Gorai
              </span>
              <p>The robotics platform for the AI era.</p>
              <p className="gorai-footer-tagline">Build robots like software. Run them like systems.</p>
            </div>
            <div className="gorai-footer-links">
              <div className="gorai-footer-col">
                <h4>Documentation</h4>
                <Link to="/gorai/docs">Getting Started</Link>
                <Link to="/gorai/docs/designs">Framework Designs</Link>
              </div>
              <div className="gorai-footer-col">
                <h4>Info</h4>
                <Link to="/gorai/info">Strategic Summary</Link>
                <Link to="/gorai/info/comparison">Framework Comparison</Link>
              </div>
              <div className="gorai-footer-col">
                <h4>Project</h4>
                <a href="https://github.com/emergingrobotics/gorai" target="_blank" rel="noopener noreferrer">GitHub</a>
                <Link to="/">Emerging Robotics</Link>
              </div>
            </div>
          </div>
          <div className="gorai-footer-bottom">
            <p>© 2025-2026 Emerging Robotics</p>
            <p>Apache 2.0 License • Built with Go</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GoraiLandingPage;

